import axios from "axios"
import { useState } from "react"
import { useSelector } from "react-redux"
import { motion, AnimatePresence } from "framer-motion"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  IconButton,
  CircularProgress
} from "@mui/material"
// lucide-react v1 REMOVED its brand icons — `Instagram` and `Twitter` are no longer exported, which
// broke the production build outright. Neither lucide nor @mui/icons-material ships brand marks, so
// each platform falls back to a neutral glyph that reads as its medium (photo / post / audio).
// Restoring real brand marks means a new dependency or vendored SVGs — a product call, not a build fix.
import {
  Camera as Instagram,
  MessageCircle as Twitter,
  Music2 as TikTok,
  DoorClosedIcon as CloseIcon
} from "lucide-react"

export default function SocialMediaWorkoutModal({ open, onClose }) {
  console.log('open', open)
  const theme = useTheme()
  const { token } = useSelector((state) => state.authSlice)
  const [link, setLink] = useState("")
  const [platform, setPlatform] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  const detectPlatform = (url) => {
    if (url.includes("instagram.com")) return "instagram"
    if (url.includes("twitter.com") || url.includes("x.com")) return "twitter"
    if (url.includes("tiktok.com")) return "tiktok"
    if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube"
    return null
  }

  const handleLinkChange = (e) => {
    const value = e.target.value
    setLink(value)
    if (value) {
      setPlatform(detectPlatform(value))
    } else {
      setPlatform(null)
    }
    setError("")
    setSuccessMessage("")
  }

  const submitSocialUrl = async (socialUrl) => {
    const result = await axios.post(
      `/api/ai/workout`,
      { socialUrl },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return result
  }

  const handleSubmit = async () => {
    if (!link) {
      setError("Please enter a social media link")
      return
    }

    const detected = detectPlatform(link)
    if (!detected) {
      setError("Only Instagram, X, TikTok, or YouTube links are supported")
      return
    }

    setIsSubmitting(true)
    setError("")
    setSuccessMessage("")

    try {
      const response = await submitSocialUrl(link)
      if (response.status === 200) {
        setSuccessMessage("✅ Your workout will appear in your templates shortly. Best go to the gym 💪. Check the side menu for quick access to your workout templates!")
        setLink("")
        setPlatform(null)
      } else {
        setError("Something went wrong. Please try again.")
      }
    } catch (err) {
      setError("Failed to generate workout. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const getPlatformIcon = () => {
    switch (platform) {
      case "instagram":
        return <Instagram size={24} />
      case "twitter":
        return <Twitter size={24} />
      case "tiktok":
        return <TikTok size={24} />
      default:
        return null
    }
  }

  const modalVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      scale: 0.9,
      transition: {
        duration: 0.2,
      },
    },
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="social-media-workout-modal"
      closeAfterTransition
    >
      <AnimatePresence>
        {open && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: theme.zIndex.modal,
              pointerEvents: "none",
            }}
          >
            <Box
              component={motion.div}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              sx={{
                width: { xs: "90%", sm: 500 },
                bgcolor: "background.paper",
                borderRadius: 4,
                boxShadow: 24,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                pointerEvents: "auto",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: `linear-gradient(90deg, #F2E600 0%, ${
                    theme.palette.tertiary?.main
                  } 100%)`,
                  p: 3,
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={onClose}
                  disabled={isSubmitting}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "#0A0A0A",
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <CloseIcon size={20} />
                </IconButton>

                <Typography
                  variant="h5"
                  component={motion.h2}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  sx={{
                    color: "#0A0A0A",
                    fontWeight: "bold",
                    mb: 1,
                  }}
                >
                  AI Workout Generator
                </Typography>

                <Typography
                  variant="body1"
                  component={motion.p}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  sx={{
                    color: "#0A0A0A",
                    opacity: 0.9,
                  }}
                >
                  Paste a social media link to generate a personalized workout
                </Typography>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                <TextField
                  fullWidth
                  label="Social Media Link"
                  variant="outlined"
                  value={link}
                  onChange={handleLinkChange}
                  error={!!error}
                  helperText={error}
                  placeholder="https://instagram.com/fitness_post"
                  slotProps={{ input: {
                    startAdornment: platform && (
                      <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>{getPlatformIcon()}</Box>
                    ),
                  } }}
                  disabled={isSubmitting || !!successMessage}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !!successMessage}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontSize: "1rem",
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Generate Workout"}
                  </Button>
                </motion.div>

                {successMessage && (
                  <Typography
                    variant="body2"
                    color="success.main"
                    align="center"
                    sx={{ mt: 2, fontWeight: 500 }}
                    component={motion.p}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {successMessage}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Modal>
  )
}
